import {
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  inputBaseClasses,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ReactComponent as Logo } from "../../svg/logo.svg";
import VersionTag from "../../components/VersionTag";
import { useAuth } from "../../store/auth/hooks";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Clear } from "@mui/icons-material";
import { useEffect, useState } from "react";

const LoginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required.")
    .pipe(z.email("Must be a valid email.")),
  password: z.string().trim().nonempty({ message: "Password is required." }),
});

export default function LoginPage() {
  const theme = useTheme();
  const auth = useAuth();
  const [showAuthAlert, setShowAuthAlert] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    trigger,
  } = useForm({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "all",
  });

  const onSubmit = async (data) => {
    auth.setEmail(data.email);
    auth.setPassword(data.password);
    auth.login(data.email, data.password);
  };

  const handleEmailClear = () => {
    setValue("email", "", { shouldDirty: true });
    trigger("email");
  };

  const handlePasswordClear = () => {
    setValue("password", "", { shouldDirty: true });
    trigger("password");
  };

  useEffect(() => {
    if (auth.hasAuthError || auth.hasLicenseError) {
      setShowAuthAlert(true);

      const timer = setTimeout(() => {
        handleClose();
      }, 3500);

      return () => clearTimeout(timer);
    }
  }, [auth.hasAuthError, auth.hasLicenseError]);

  const handleClose = () => setShowAuthAlert(false);

  return (
    <Grid container component="main" height="100vh">
      <Grid
        display="flex"
        flexDirection="column"
        size={{ xs: 12, sm: 3 }}
        height={{ xs: 0.4, sm: 1 }}
        sx={{ backgroundColor: theme.palette.primary.main }}
      >
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          height={1}
          justifyContent="space-between"
        >
          <Stack
            justifyContent="center"
            height={1}
            width={{ xs: 0.5, sm: 0.8 }}
            maxWidth={{ xs: 180, sm: 240 }}
          >
            <Logo width="100%" />
          </Stack>
          <VersionTag textColorLight />
        </Box>
      </Grid>
      <Grid
        display="flex"
        component="form"
        flexDirection="column"
        height={{ xs: 0.6, sm: 1 }}
        onSubmit={handleSubmit(onSubmit)}
        size={{ xs: 12, sm: 9 }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={1}
        >
          <Grid container direction="column" p={4} maxWidth={500} width={1}>
            <Typography mb={1} variant="h4">
              Sign In
            </Typography>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.email}
                  fullWidth
                  helperText={
                    errors.email
                      ? errors.email.message
                      : "Please enter a valid email address."
                  }
                  label="Email Address"
                  margin="normal"
                  required
                  type="email"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{
                            [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                              {
                                opacity: 0,
                                pointerEvents: "none",
                              },
                          }}
                        >
                          <IconButton
                            aria-label="clear email input"
                            onClick={handleEmailClear}
                            edge="end"
                            size="small"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.password}
                  fullWidth
                  helperText={
                    errors.password
                      ? errors.password.message
                      : "Please enter a password."
                  }
                  label="Password"
                  margin="normal"
                  required
                  type="password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{
                            [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                              {
                                opacity: 0,
                                pointerEvents: "none",
                              },
                          }}
                        >
                          <IconButton
                            aria-label="clear password input"
                            onClick={handlePasswordClear}
                            edge="end"
                            size="small"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <Collapse in={showAuthAlert && auth.hasAuthError}>
              <Alert severity="error" sx={{ boxShadow: 2, my: 2 }}>
                Invalid username or password.
              </Alert>
            </Collapse>
            <Collapse in={showAuthAlert && auth.hasLicenseError}>
              <Alert severity="error" sx={{ boxShadow: 2, my: 2 }}>
                The maximum number of users are currently logged in. Please try
                again later.
              </Alert>
            </Collapse>
            <Button
              disabled={!isValid}
              variant="contained"
              sx={{ mt: 2, width: 1 }}
              type="submit"
            >
              SIGN IN
            </Button>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
}
